/*
  The app bar (§3.2, §3.4, §6.2, D14, D16), rebuilt in Tailwind.

  FIXED DARK IN BOTH THEMES (D14). The gradient and the greys are literals rather than
  tokens, and every foreground on them is `pcg-onAccent`: Tailwind's `white` is pointed at
  `--pcg-fg` (tailwind.config.js), so `text-white` here would remap to near-black on
  paper. The logo PNG is unchanged and is never inverted.

  **The dropdown panel is fixed dark too.** D22's "inner panels follow the page theme" is
  a named exception for the cert-lookup hero's four panels and is not extended by analogy:
  D14 says the AppBar keeps its treatment, and §12.15 asks for a bar that is identical in
  both themes apart from the toggle's own state. The panel is part of the bar.

  MUI's <Menu> was doing four things for free that a plain div does not, and §7.4 requires
  all four back: `aria-expanded` / `aria-controls` on the trigger, Escape closes, focus
  returns to the trigger, and arrow keys move between items. The closed panel is hidden
  rather than unmounted, so `aria-controls` keeps pointing at something real; `invisible`
  is its visual state and `inert` — set in the layout effect below — is what takes its
  links out of the tab order and the accessibility tree.

  The menu also stops holding the page hostage. Layout used to render <></> for the whole
  site while /v1/gameInfo was in flight and a silently empty menu if it failed; the two
  states live here now, next to the query that produces them (§6.2, §12.20).
*/

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { KeyboardEvent as ReactKeyboardEvent } from "react";
import { Link } from "react-router-dom";
import { Loader2, Menu, Moon, Sun } from "lucide-react";

import { APPBAR_HEIGHT, APPBAR_LOGO_HEIGHT } from "constants/layout";

import { useGetCountsQuery, useGetGameOptions } from "services/pcgPublic";
import { formatCount } from "lib";
import { useTheme } from "theme";

import LogoImage from "images/pcg-logo.png";
import { PAGE_CONTAINER } from "components/styles";
import HeaderStatsTicker, { useHeaderStats } from "components/HeaderStatsTicker";
import CategoryToggle from "components/CategoryToggle";
import { useHomeCategory } from "components/useHomeCategory";
import { useOfficeChoice } from "components/useOfficeChoice";

const MENU_ID = "pcg-appbar-game-menu";

/* The two literals the bar is built from — see the D14 note above. */
const BAR_DARK = "#212121";
const BAR_GRADIENT = `linear-gradient(315deg, #424242 0%, ${BAR_DARK} 94%)`;

/*
  The chrome's own focus ring. Not `FOCUS_RING` from components/styles: that one rings in
  goldText and offsets against `pcg-card`, both of which follow the page theme, and this
  surface does not. Brand gold on the bar's #212121 is 8.75:1, well past WCAG 1.4.11's
  3:1 for a non-text indicator.

  It rings in `goldChrome`, not `gold`. Since phase 2 the fill token is #D8A24A in dark
  and #DEBB68 in light, so `ring-pcg-gold` would have made this ring — on a bar that is
  the same greys in both themes — change colour with the page. goldChrome does not.
*/
const CHROME_FOCUS_RING =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pcg-goldChrome";

const CHROME_BUTTON =
  `inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-pcg-onAccent ` +
  `hover:bg-[rgba(255,255,255,0.12)] disabled:cursor-not-allowed disabled:opacity-50 ` +
  `disabled:hover:bg-transparent ${CHROME_FOCUS_RING}`;

/*
  The bar's gold call to action, the storefront header's "Submit" (Robert, 2026-10-20: the bar
  "feels a little bit empty"). Black on brand gold, 11.4:1, in both themes.
*/
const SUBMIT_BUTTON =
  `inline-flex h-10 shrink-0 items-center rounded-md bg-pcg-goldChrome px-4 text-sm font-bold ` +
  `text-black transition-[filter] hover:brightness-110 focus-visible:outline-none ` +
  `focus-visible:ring-2 focus-visible:ring-pcg-goldChrome focus-visible:ring-offset-2 ` +
  `focus-visible:ring-offset-[#0B0E14]`;

/* Marks the focusable menu links, so arrow-key navigation can find them in DOM order. */
const MENU_ITEM_ATTR = "data-appbar-menu-item";

const ResponsiveAppBar = () => {
  const { options: allOptions, isLoading, isError } = useGetGameOptions();
  /*
    The menu lists one side of the TCG | Sports toggle at its top (docs/specs/
    pop-report-batch3.md D1, D2). The same choice drives the home page's Browse by Game.
  */
  const [category] = useHomeCategory();
  const options = allOptions.filter((game) => game.category === category);
  /* The live total beside the CTA. Shares the home card's /counts request (RTK Query). */
  const counts = useGetCountsQuery();
  const liveTotal = counts.data?.totalGraded;
  /* The live stats ticker's items, computed once for both of its placements below. */
  const stats = useHeaderStats();
  const { theme, toggleTheme } = useTheme();
  /*
    D23 of docs/specs/pop-language-split.md (Robert, 2026-10-20): Submit cards goes to the
    visitor's remembered office, else the one their time zone suggests first, else
    premiercardgrading.com as before. It follows an office chosen in the footer at once.
  */
  const { submitHref } = useOfficeChoice();

  const [open, setOpen] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const menuItems = () =>
    Array.from(
      menuRef.current?.querySelectorAll<HTMLElement>(`[${MENU_ITEM_ATTR}]`) ?? []
    );

  /*
    `restoreFocus` is the difference between the visitor dismissing the menu — Escape, or
    choosing an item — and the menu closing because they went somewhere else. Only the
    first should pull focus back to the trigger; doing it on a click elsewhere would yank
    the caret out of whatever they just clicked on.
  */
  const closeMenu = useCallback((restoreFocus: boolean) => {
    setOpen(false);
    if (restoreFocus) {
      triggerRef.current?.focus();
    }
  }, []);

  /*
    Dismissal, the same shape the hero uses (SearchCardsRow): mousedown and focusin in the
    capture phase so nothing inside can strand the panel open, plus Escape.
  */
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const isOutside = (target: EventTarget | null) =>
      target instanceof Node && !wrapperRef.current?.contains(target);

    const handlePointerDown = (event: MouseEvent) => {
      if (isOutside(event.target)) {
        closeMenu(false);
      }
    };
    const handleFocusIn = (event: FocusEvent) => {
      if (isOutside(event.target)) {
        closeMenu(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeMenu(true);
      }
    };

    document.addEventListener("mousedown", handlePointerDown, true);
    document.addEventListener("focusin", handleFocusIn, true);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown, true);
      document.removeEventListener("focusin", handleFocusIn, true);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, closeMenu]);

  /*
    Opening moves focus to the first game, as MUI's Menu did. Deliberately keyed on `open`
    alone: if it also watched the option list, games arriving over the network while the
    menu sat open would steal focus back to the top mid-keystroke.
  */
  useEffect(() => {
    if (!open) {
      return;
    }
    menuItems()[0]?.focus();
  }, [open]);

  /*
    The closed panel carried `aria-hidden={!open}` while still holding the game links —
    the pattern !9 took out of SearchCardsRow, and the same remedy.

    One honest difference from !9, so nobody re-derives it: this one was not yet firing.
    SearchCardsRow transitioned with `transition-all`, and `visibility` interpolates
    discretely in the visible direction, so its fields stayed genuinely tabbable under
    `aria-hidden` for the whole 200ms close — a live `aria-hidden-focus`. Here the
    transition is `transition-opacity`, which never touches `visibility`, so `invisible`
    lands the instant `open` flips and axe 4.4.1 scores the closed panel clean. Confirmed
    both ways in the browser: the old markup passed the rule, and forcing the same nav
    visible while `aria-hidden` made it fail.

    It is removed anyway, because what `invisible` was holding up is an accident of this
    component's transition list. A container holding focusable children is the one place
    `aria-hidden` must never be used: it tells screen readers the subtree is not there
    while doing nothing to keep a keyboard out of it, so the two only agree for as long
    as some *other* property happens to be hiding the children. Add a `translate` or swap
    in `transition-all` and this becomes !9's bug, with no warning.

    `inert` is the primitive that actually means "not here": it removes the subtree from
    BOTH the tab order and the accessibility tree, in one attribute, without leaning on
    the visual state. Verified here by forcing the closed nav visible while inert — the
    links still refused focus. That makes `aria-hidden` redundant as well as wrong, which
    is why it is gone rather than kept alongside; `invisible` stays on as the visual state
    and as the fallback for anything too old for `inert`.

    Set through the DOM because React 17 has no `inert` prop and TypeScript 4.6's lib.dom
    has no `inert` property; the attribute is what drives the behaviour in any case.
    Layout effect, not effect, so it lands before the closing frame is painted — and, on
    open, before the passive effect above moves focus to the first game.
  */
  useLayoutEffect(() => {
    const menu = menuRef.current;
    if (!menu) {
      return;
    }

    if (open) {
      menu.removeAttribute("inert");
      return;
    }

    /* Inerting the subtree focus is sitting in would strand it on <body>. */
    if (menu.contains(document.activeElement)) {
      triggerRef.current?.focus();
    }
    menu.setAttribute("inert", "");
  }, [open]);

  const handleTriggerKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
    }
  };

  const handleMenuKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const items = menuItems();
    if (items.length === 0) {
      return;
    }
    const current = items.indexOf(document.activeElement as HTMLElement);

    if (event.key === "ArrowDown") {
      event.preventDefault();
      items[(current + 1 + items.length) % items.length].focus();
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      /* From the TCG | Sports toggle (not an item, so -1) ArrowUp goes to the last game. */
      items[current === -1 ? items.length - 1 : (current - 1 + items.length) % items.length].focus();
    } else if (event.key === "Home") {
      event.preventDefault();
      items[0].focus();
    } else if (event.key === "End") {
      event.preventDefault();
      items[items.length - 1].focus();
    }
  };

  /* The trigger says what it is in each of the three states (§6.2, §12.20). */
  const triggerLabel = isError
    ? "Games unavailable"
    : isLoading
    ? "Game options, loading"
    : "Game options";

  const themeLabel =
    theme === "dark" ? "Switch to light theme" : "Switch to dark theme";

  return (
    /*
      `relative z-30` so the panel hangs over the hero below it, whose own overlays sit at
      z-10 and z-20 and would otherwise paint over a menu that comes earlier in the DOM.
    */
    /*
      `skin-appbar` lets the event skin swap the bar's gradient for the storefront
      header's flat #0B0E14 (src/skins/event.css). Inert in navy and gilded.
    */
    <>
    <header
      className="skin-appbar relative z-30 w-full"
      style={{
        height: APPBAR_HEIGHT,
        backgroundColor: BAR_DARK,
        backgroundImage: BAR_GRADIENT,
      }}
    >
      {/*
        Laid out like the storefront header (Robert, 2026-10-20): the PCG mark at the left
        with the games menu beside it, and on the right a live "cards graded" figure, the
        theme toggle and a gold Submit button.
      */}
      <div className={`${PAGE_CONTAINER} flex h-full items-center justify-between gap-2`}>
        <div className="flex h-full shrink-0 items-center gap-4 sm:gap-6">
        <Link to="/" aria-label="Premier Card Grading — home" className={`flex shrink-0 items-center rounded-md ${CHROME_FOCUS_RING}`}>
          <img
            src={LogoImage}
            alt=""
            aria-hidden="true"
            className="w-auto"
            style={{ height: APPBAR_LOGO_HEIGHT }}
          />
        </Link>
        <div ref={wrapperRef} className="relative flex h-full items-center gap-2">
          <button
            ref={triggerRef}
            type="button"
            aria-expanded={open}
            aria-controls={MENU_ID}
            aria-label={triggerLabel}
            title={triggerLabel}
            disabled={isError}
            onClick={() => setOpen((current) => !current)}
            onKeyDown={handleTriggerKeyDown}
            className={`${CHROME_BUTTON} sm:w-auto sm:gap-2 sm:px-3`}
          >
            {isLoading ? (
              <Loader2 aria-hidden="true" className="h-6 w-6 animate-spin" />
            ) : (
              <Menu aria-hidden="true" className="h-6 w-6" />
            )}
            <span aria-hidden="true" className="hidden text-sm font-semibold sm:inline">
              Games
            </span>
          </button>

          {/*
            The failure, said out loud and shown on screen. A disabled button is not
            focusable, so its aria-label alone would never reach a screen reader; the
            role="status" is what actually announces the failure, and it is visible text
            rather than sr-only so §12.20's evidence is something a human can see.
          */}
          {isError ? (
            <span
              role="status"
              className="text-xs font-medium text-pcg-onAccent/80"
            >
              Games unavailable
            </span>
          ) : null}

          <nav
            id={MENU_ID}
            ref={menuRef}
            aria-label="Games"
            onKeyDown={handleMenuKeyDown}
            className={
              `absolute left-0 top-full z-40 mt-1 max-h-[70vh] w-64 overflow-y-auto ` +
              `rounded-md border border-[rgba(255,255,255,0.16)] py-1 shadow-xl ` +
              `transition-opacity duration-150 ${
                open ? "visible opacity-100" : "invisible opacity-0"
              }`
            }
            style={{ backgroundColor: BAR_DARK }}
          >
            {/*
              The TCG | Sports toggle, pinned while the list scrolls under it. Its buttons
              are reached with Tab and are not menu items, so the arrow keys still walk
              the games alone. A click on it is inside the wrapper, so the menu stays open.
            */}
            {!isLoading && allOptions.length > 0 ? (
              <CategoryToggle
                variant="chrome"
                className="sticky top-0 z-10 -mt-1 mb-1 border-b border-[rgba(255,255,255,0.16)] p-2"
                style={{ backgroundColor: BAR_DARK }}
              />
            ) : null}

            {isLoading ? (
              <p
                role="status"
                className="px-4 py-2 text-sm text-pcg-onAccent/80"
              >
                Loading games…
              </p>
            ) : null}

            {!isLoading && options.length === 0 ? (
              <p className="px-4 py-2 text-sm text-pcg-onAccent/80">
                {allOptions.length > 0 ? `No ${category} games` : "No games available"}
              </p>
            ) : null}

            {options.map((game) => (
              <Link
                key={game.id}
                to={`/${game.gameCode}`}
                data-appbar-menu-item=""
                onClick={() => closeMenu(true)}
                /*
                  scroll-mt-16 clears the ~56px sticky toggle, so a game the arrow keys
                  scroll to the top is not hidden under it (WCAG 2.4.11).
                */
                className={
                  `block scroll-mt-16 px-4 py-2 text-sm text-pcg-onAccent no-underline ` +
                  `hover:bg-[rgba(255,255,255,0.12)] ${CHROME_FOCUS_RING} ` +
                  `focus-visible:ring-inset`
                }
              >
                {game.name}
                {/* Two games can share a name; the code tells them apart. */}
                {game.duplicateName ? (
                  <span className="ml-1.5 text-xs text-pcg-onAccent/60">{game.gameCode}</span>
                ) : null}
              </Link>
            ))}
          </nav>
        </div>
        </div>

        {/*
          The live stats ticker fills the middle of the bar from xl (Robert, 2026-10-20).
          Narrower, there is no room beside the menu and the CTA, so it drops to its own
          row under the bar instead (below).
        */}
        <HeaderStatsTicker stats={stats} className="mx-2 hidden flex-1 xl:flex 2xl:mx-6" />

        {/*
          gap-5 rather than gap-1. At 4px the theme toggle sat almost against the logo and
          read as part of the mark rather than as a control of its own; the two clusters in
          this bar need to look like two things.
        */}
        <div className="flex shrink-0 items-center gap-3 sm:gap-5">
          {typeof liveTotal === "number" && isFinite(liveTotal) ? (
            <p className="hidden items-center gap-2 text-sm text-pcg-onAccent/80 lg:flex">
              <span aria-hidden="true" className="h-2 w-2 animate-pulse rounded-full bg-pcg-danger motion-reduce:animate-none" />
              <span className="font-bold tabular-nums text-pcg-goldChrome">{formatCount(liveTotal)}</span>
              cards graded
            </p>
          ) : null}
          {/*
            The theme toggle (§3.2, §12.14). The label names the theme it switches TO, so
            it is an instruction rather than a status; the icon shows the same thing.
          */}
          <button
            type="button"
            onClick={toggleTheme}
            aria-label={themeLabel}
            title={themeLabel}
            className={CHROME_BUTTON}
          >
            {theme === "dark" ? (
              <Sun aria-hidden="true" className="h-5 w-5" />
            ) : (
              <Moon aria-hidden="true" className="h-5 w-5" />
            )}
          </button>

          <a href={submitHref} className={SUBMIT_BUTTON}>
            Submit cards
          </a>
        </div>
      </div>
    </header>
    {/*
      Below xl the ticker is a slim second row of the same fixed-dark chrome. `skin-appbar`
      gives it the event skin's flat navy and hairline, the same as the bar above.
      The row keeps its 40px while the stats load, so the page below does not jump when they
      arrive.
    */}
    <div
      className="skin-appbar h-10 w-full xl:hidden"
      style={{ backgroundColor: BAR_DARK, backgroundImage: BAR_GRADIENT }}
    >
      <div className={`${PAGE_CONTAINER} h-full`}>
        <HeaderStatsTicker stats={stats} className="flex h-full" />
      </div>
    </div>
    </>
  );
};

export default ResponsiveAppBar;
